import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';
import FinalizePasswordReset from './FinalizePasswordReset';
import { ChevronLeft } from 'lucide-react';

const AuthActionHandler = () => {
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  
  useEffect(() => {
    // Parse the URL parameters
    const urlParams = new URLSearchParams(location.search);
    const actionMode = urlParams.get('mode');
    
    if (!actionMode) {
      setError('Invalid action link. No action mode specified.');
    } else {
      setMode(actionMode);
    }
    setLoading(false);
  }, [location]);
  
  // Determine if we're in the native app environment
  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  
  // Loading state with branded styling
  if (loading) {
    return (
      <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
        isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
      }`}>
        {/* Background styling */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
            : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
        }`} />
        
        <div className="relative flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
            <p className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
        isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
      }`}>
        {/* Background styling */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
            : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
        }`} />
        
        {/* Back Button */}
        <div className="absolute top-0 left-0 z-10 m-4" style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top))' : '0' }}>
          <button
            onClick={() => navigate('/login')}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
              isDarkMode 
                ? 'bg-white/10 hover:bg-white/20 text-white' 
                : 'bg-black/10 hover:bg-black/20 text-black'
            }`}
          >
            <ChevronLeft size={20} />
          </button>
        </div>
        
        <div className="relative flex-1 flex items-center justify-center px-4">
          <div className={`w-full max-w-md rounded-[24px] overflow-hidden ${
            isDarkMode 
              ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
              : 'bg-white shadow-sm border border-gray-200'
          }`}>
            <div className="p-8 text-center">
              <h2 className={`text-2xl font-light mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Invalid Link</h2>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {error}
              </p>
              <button
                onClick={() => navigate('/login')}
                className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                Return to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Render the appropriate component based on the action mode
  switch (mode) {
    case 'resetPassword':
      return <FinalizePasswordReset />;
    case 'verifyEmail':
      return <EmailVerificationHandler />;
    case 'recoverEmail':
      return <EmailRecoveryHandler />;
    default:
      return (
        <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
          isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
        }`}>
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
              : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
          }`} />
          
          <div className="relative flex-1 flex items-center justify-center px-4">
            <div className={`w-full max-w-md rounded-[24px] overflow-hidden ${
              isDarkMode 
                ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
                : 'bg-white shadow-sm border border-gray-200'
            }`}>
              <div className="p-8 text-center">
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Unsupported Action</h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  This action type ({mode}) is not supported.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Return to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      );
  }
};

// Email Verification Handler (placeholder for now)
const EmailVerificationHandler = () => {
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const oobCode = urlParams.get('oobCode');
        
        if (!oobCode) {
          setError("Invalid verification link. Missing verification code.");
          setVerifying(false);
          return;
        }
        
        // Import applyActionCode only when needed to avoid circular dependencies
        const { applyActionCode } = await import('firebase/auth');
        const { auth } = await import('../firebase');
        
        await applyActionCode(auth, oobCode);
        setSuccess(true);
        setVerifying(false);
      } catch (error) {
        console.error("Error verifying email:", error);
        setError(error.message || "Failed to verify your email. The link may have expired.");
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [location]);
  
  return (
    <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {/* Background styling */}
      <div className={`absolute inset-0 ${
        isDarkMode 
          ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
          : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
      }`} />
      
      {!verifying && (
        <div className="absolute top-0 left-0 z-10 m-4" style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top))' : '0' }}>
          <button
            onClick={() => navigate('/login')}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
              isDarkMode 
                ? 'bg-white/10 hover:bg-white/20 text-white' 
                : 'bg-black/10 hover:bg-black/20 text-black'
            }`}
          >
            <ChevronLeft size={20} />
          </button>
        </div>
      )}
      
      <div className="relative flex-1 flex items-center justify-center px-4">
        <div className={`w-full max-w-md rounded-[24px] overflow-hidden ${
          isDarkMode 
            ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
            : 'bg-white shadow-sm border border-gray-200'
        }`}>
          <div className="p-8 text-center">
            {verifying ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Verifying Your Email</h2>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Please wait while we confirm your email address...
                </p>
              </>
            ) : success ? (
              <>
                <svg 
                  className="w-16 h-16 text-emerald-500 mx-auto mb-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Email Verified!</h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your email has been successfully verified. You can now use all features of your account.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Go to Dashboard
                </button>
              </>
            ) : (
              <>
                <svg 
                  className="w-16 h-16 text-red-500 mx-auto mb-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Verification Failed</h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {error}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Return to Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Recovery Handler (placeholder for now)
const EmailRecoveryHandler = () => {
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  
  useEffect(() => {
    const recoverEmail = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const oobCode = urlParams.get('oobCode');
        
        if (!oobCode) {
          setError("Invalid recovery link. Missing recovery code.");
          setProcessing(false);
          return;
        }
        
        // Import checkActionCode and applyActionCode only when needed
        const { checkActionCode, applyActionCode } = await import('firebase/auth');
        const { auth } = await import('../firebase');
        
        // First check the action code
        await checkActionCode(auth, oobCode);
        
        // Then apply it
        await applyActionCode(auth, oobCode);
        
        setSuccess(true);
        setProcessing(false);
      } catch (error) {
        console.error("Error recovering email:", error);
        setError(error.message || "Failed to recover your email. The link may have expired.");
        setProcessing(false);
      }
    };
    
    recoverEmail();
  }, [location]);
  
  return (
    <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {/* Background styling */}
      <div className={`absolute inset-0 ${
        isDarkMode 
          ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
          : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
      }`} />
      
      {!processing && (
        <div className="absolute top-0 left-0 z-10 m-4" style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top))' : '0' }}>
          <button
            onClick={() => navigate('/login')}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
              isDarkMode 
                ? 'bg-white/10 hover:bg-white/20 text-white' 
                : 'bg-black/10 hover:bg-black/20 text-black'
            }`}
          >
            <ChevronLeft size={20} />
          </button>
        </div>
      )}
      
      <div className="relative flex-1 flex items-center justify-center px-4">
        <div className={`w-full max-w-md rounded-[24px] overflow-hidden ${
          isDarkMode 
            ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
            : 'bg-white shadow-sm border border-gray-200'
        }`}>
          <div className="p-8 text-center">
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Recovering Your Email</h2>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Please wait while we restore your email address...
                </p>
              </>
            ) : success ? (
              <>
                <svg 
                  className="w-16 h-16 text-emerald-500 mx-auto mb-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Email Recovered!</h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your email has been successfully restored. You can now log in with your original email address.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Go to Login
                </button>
              </>
            ) : (
              <>
                <svg 
                  className="w-16 h-16 text-red-500 mx-auto mb-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
                <h2 className={`text-2xl font-light mb-4 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Recovery Failed</h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {error}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Return to Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthActionHandler; 